<template>
    <div
        v-if="isVisible"
        class="notification-permission"
        role="dialog"
        aria-modal="true"
        aria-labelledby="notification-permission-title"
        aria-describedby="notification-permission-description"
    >
        <section class="notification-permission__card">
            <div class="notification-permission__icon" aria-hidden="true">
                <i class="fas fa-bell"></i>
            </div>

            <p class="notification-permission__eyebrow">Первый запуск</p>
            <h2 id="notification-permission-title">
                {{ promptTitle }}
            </h2>
            <p
                id="notification-permission-description"
                class="notification-permission__description"
            >
                Аргус сможет сообщать на этом устройстве о задачах и заявках,
                которые требуют вашего внимания. Конкретные виды уведомлений
                мы настроим отдельно.
            </p>

            <p
                v-if="errorMessage"
                class="notification-permission__error"
                role="alert"
            >
                {{ errorMessage }}
            </p>

            <button
                ref="allowButton"
                type="button"
                class="notification-permission__allow"
                :disabled="isRequesting"
                @click="enableNotifications"
            >
                <span
                    v-if="isRequesting"
                    class="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                ></span>
                <i v-else class="fas fa-bell" aria-hidden="true"></i>
                {{ allowButtonLabel }}
            </button>

            <button
                type="button"
                class="notification-permission__skip"
                :disabled="isRequesting"
                @click="skipPermission"
            >
                Продолжить без уведомлений
            </button>
        </section>
    </div>
</template>

<script>
import {
    clearNotificationOnboardingState,
    enableWebPushForCurrentDevice,
    isNotificationOnboardingSuppressed,
    prepareWebPushDevice,
    shouldOfferNotificationOnboarding,
    snoozeNotificationOnboarding
} from '@/utils/notificationPermission';
import {
    INSTALLATION_STATE_CHANGED_EVENT
} from '@/utils/appInstallation';

const RETRY_DELAYS_MS = [2000, 5000, 15000];

export default {
    name: 'NotificationPermissionPrompt',
    data() {
        return {
            isVisible: false,
            isRequesting: false,
            errorMessage: '',
            deviceState: null,
            evaluationId: 0,
            synchronizedIdentity: null,
            retryTimer: null,
            retryAttempt: 0,
            evaluationQueued: false,
            queuedForce: false
        };
    },
    computed: {
        isAuthorized() {
            return this.$store.getters.isAuthorized;
        },
        isPlatformRoute() {
            return this.$route.matched.some(({meta}) => meta.platform === true);
        },
        isTelegramWebApp() {
            return this.$store.getters.isTelegramWebApps;
        },
        currentIdentity() {
            const user = this.$store.getters.currentUser;
            return user
                ? `${user.company || ''}:${user.userId || ''}`
                : null;
        },
        onboardingContext() {
            return {
                identity: this.currentIdentity
            };
        },
        promptTitle() {
            return this.deviceState && [
                'granted-unsubscribed',
                'subscription-error'
            ].includes(this.deviceState.status)
                ? 'Завершите подключение уведомлений'
                : 'Разрешите уведомления';
        },
        allowButtonLabel() {
            if (this.isRequesting) {
                return 'Подключаем…';
            }
            return this.deviceState && [
                'granted-unsubscribed',
                'subscription-error'
            ].includes(this.deviceState.status)
                ? 'Завершить подключение'
                : 'Разрешить уведомления';
        }
    },
    watch: {
        isAuthorized(value, previousValue) {
            if (!value) {
                this.isVisible = false;
                this.synchronizedIdentity = null;
                this.clearRetryTimer();
                return;
            }
            if (!previousValue) {
                this.retryAttempt = 0;
                this.queueVisibilityEvaluation({force: true});
            }
        },
        currentIdentity(value, previousValue) {
            if (value !== previousValue) {
                this.synchronizedIdentity = null;
                this.retryAttempt = 0;
                this.queueVisibilityEvaluation({force: true});
            }
        },
        '$route.fullPath'() {
            this.queueVisibilityEvaluation();
        }
    },
    mounted() {
        window.addEventListener('focus', this.onLifecycleRetry);
        window.addEventListener('online', this.onLifecycleRetry);
        window.addEventListener('pageshow', this.onLifecycleRetry);
        window.addEventListener(
            INSTALLATION_STATE_CHANGED_EVENT,
            this.onLifecycleRetry
        );
        document.addEventListener(
            'visibilitychange',
            this.onVisibilityChange
        );
        this.queueVisibilityEvaluation();
    },
    beforeDestroy() {
        this.clearRetryTimer();
        window.removeEventListener('focus', this.onLifecycleRetry);
        window.removeEventListener('online', this.onLifecycleRetry);
        window.removeEventListener('pageshow', this.onLifecycleRetry);
        window.removeEventListener(
            INSTALLATION_STATE_CHANGED_EVENT,
            this.onLifecycleRetry
        );
        document.removeEventListener(
            'visibilitychange',
            this.onVisibilityChange
        );
    },
    methods: {
        async evaluateVisibility(options = {}) {
            const evaluationId = ++this.evaluationId;
            this.isVisible = false;
            this.errorMessage = '';

            if (
                !this.isAuthorized
                || this.isPlatformRoute
                || this.isTelegramWebApp
            ) {
                if (!this.isAuthorized) {
                    this.synchronizedIdentity = null;
                }
                return;
            }
            if (
                this.currentIdentity
                && isNotificationOnboardingSuppressed(
                    this.onboardingContext
                )
            ) {
                this.clearRetryTimer();
                return;
            }
            if (
                this.currentIdentity
                && this.synchronizedIdentity === this.currentIdentity
                && options.force !== true
                && this.deviceState
                && this.deviceState.status === 'subscribed'
            ) {
                return;
            }

            let deviceState;
            try {
                deviceState = await prepareWebPushDevice({
                    syncExisting: true,
                    force: options.force === true
                });
            } catch (error) {
                deviceState = {
                    status: 'server-unavailable',
                    subscription: null,
                    error
                };
            }
            if (evaluationId !== this.evaluationId) {
                return;
            }
            this.deviceState = deviceState;
            this.synchronizedIdentity = this.currentIdentity;
            if (deviceState.status === 'subscribed') {
                clearNotificationOnboardingState(
                    this.onboardingContext
                );
                this.retryAttempt = 0;
                this.clearRetryTimer();
            } else if (deviceState.status === 'server-unavailable') {
                this.scheduleRetry();
            } else {
                this.retryAttempt = 0;
                this.clearRetryTimer();
            }
            this.isVisible = shouldOfferNotificationOnboarding({
                isAuthorized: this.isAuthorized,
                isPlatformRoute: this.isPlatformRoute,
                deviceState,
                onboardingContext: this.onboardingContext
            });

            if (this.isVisible) {
                this.$nextTick(() => {
                    if (this.$refs.allowButton) {
                        this.$refs.allowButton.focus();
                    }
                });
            }
        },
        async enableNotifications() {
            if (this.isRequesting) {
                return;
            }

            this.isRequesting = true;
            this.errorMessage = '';

            try {
                const subscription = await enableWebPushForCurrentDevice();
                clearNotificationOnboardingState(
                    this.onboardingContext
                );
                this.deviceState = {
                    ...(this.deviceState || {}),
                    status: 'subscribed',
                    permission: 'granted',
                    subscription
                };
                this.retryAttempt = 0;
                this.clearRetryTimer();
                this.isVisible = false;
            } catch (error) {
                let state;
                try {
                    state = await prepareWebPushDevice({
                        syncExisting: true,
                        force: true
                    });
                } catch (refreshError) {
                    state = {
                        status: 'server-unavailable',
                        subscription: null,
                        error: refreshError
                    };
                }
                this.deviceState = state;
                if (state.status === 'denied') {
                    this.isVisible = false;
                } else {
                    this.errorMessage =
                        'Не удалось завершить подключение. Проверьте интернет и попробуйте ещё раз.';
                }
            } finally {
                this.isRequesting = false;
            }
        },
        skipPermission() {
            snoozeNotificationOnboarding(this.onboardingContext);
            this.isVisible = false;
        },
        scheduleRetry() {
            if (
                this.retryTimer
                || this.retryAttempt >= RETRY_DELAYS_MS.length
            ) {
                return;
            }
            const delay = RETRY_DELAYS_MS[this.retryAttempt];
            this.retryAttempt += 1;
            this.retryTimer = window.setTimeout(() => {
                this.retryTimer = null;
                this.queueVisibilityEvaluation({force: true});
            }, delay);
        },
        clearRetryTimer() {
            if (!this.retryTimer) {
                return;
            }
            window.clearTimeout(this.retryTimer);
            this.retryTimer = null;
        },
        onLifecycleRetry() {
            if (
                this.isRequesting
                || this.isVisible
                || (
                    this.deviceState
                    && this.deviceState.status === 'subscribed'
                )
                || (
                    typeof document !== 'undefined'
                    && document.visibilityState === 'hidden'
                )
            ) {
                return;
            }
            this.retryAttempt = 0;
            this.queueVisibilityEvaluation({
                force: !this.deviceState
                    || [
                        'server-unavailable',
                        'unsupported',
                        'denied'
                    ].includes(this.deviceState.status)
            });
        },
        onVisibilityChange() {
            if (document.visibilityState === 'visible') {
                this.onLifecycleRetry();
            }
        },
        queueVisibilityEvaluation(options = {}) {
            this.queuedForce = this.queuedForce
                || options.force === true;
            if (this.evaluationQueued) {
                return;
            }
            this.evaluationQueued = true;
            this.$nextTick(() => {
                const force = this.queuedForce;
                this.evaluationQueued = false;
                this.queuedForce = false;
                this.evaluateVisibility({force});
            });
        }
    }
};
</script>

<style scoped>
.notification-permission {
    position: fixed;
    inset: 0;
    z-index: 10050;
    display: flex;
    align-items: center;
    justify-content: center;
    padding:
        calc(20px + env(safe-area-inset-top))
        calc(20px + env(safe-area-inset-right))
        calc(20px + env(safe-area-inset-bottom))
        calc(20px + env(safe-area-inset-left));
    overflow-y: auto;
    background:
        radial-gradient(circle at 50% 0%, rgba(41, 121, 255, 0.2), transparent 45%),
        rgba(14, 25, 39, 0.78);
    backdrop-filter: blur(10px);
}

.notification-permission__card {
    width: min(100%, 430px);
    padding: 30px 26px 24px;
    color: #17202b;
    text-align: center;
    background: #fff;
    border: 1px solid rgba(25, 118, 255, 0.2);
    border-radius: 26px;
    box-shadow: 0 24px 80px rgba(5, 20, 39, 0.32);
}

.notification-permission__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 68px;
    height: 68px;
    margin-bottom: 18px;
    color: #fff;
    font-size: 28px;
    background: linear-gradient(145deg, #2f8cff, #1769e8);
    border-radius: 22px;
    box-shadow: 0 14px 28px rgba(23, 105, 232, 0.32);
}

.notification-permission__eyebrow {
    margin: 0 0 7px;
    color: #1769e8;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}

.notification-permission h2 {
    margin: 0;
    font-size: clamp(25px, 7vw, 32px);
    font-weight: 750;
    line-height: 1.15;
}

.notification-permission__description {
    margin: 15px 0 24px;
    color: #5f6f82;
    font-size: 16px;
    line-height: 1.5;
}

.notification-permission__error {
    margin: -8px 0 18px;
    padding: 10px 12px;
    color: #9b2c2c;
    font-size: 14px;
    background: #fff1f1;
    border-radius: 12px;
}

.notification-permission__allow,
.notification-permission__skip {
    width: 100%;
    min-height: 50px;
    border-radius: 14px;
    font-size: 16px;
    font-weight: 700;
}

.notification-permission__allow {
    color: #fff;
    background: #1976ff;
    border: 1px solid #1976ff;
    box-shadow: 0 10px 22px rgba(25, 118, 255, 0.28);
}

.notification-permission__allow:disabled,
.notification-permission__skip:disabled {
    cursor: wait;
    opacity: 0.7;
}

.notification-permission__allow i,
.notification-permission__allow .spinner-border {
    margin-right: 8px;
}

.notification-permission__skip {
    margin-top: 9px;
    color: #536273;
    background: transparent;
    border: 0;
}

.notification-permission__allow:focus-visible,
.notification-permission__skip:focus-visible {
    outline: 3px solid rgba(25, 118, 255, 0.32);
    outline-offset: 3px;
}

@media (max-width: 420px) {
    .notification-permission {
        align-items: flex-end;
        padding-right: 12px;
        padding-left: 12px;
    }

    .notification-permission__card {
        padding: 26px 20px 18px;
        border-radius: 24px;
    }
}
</style>
